/**
 * PerformanceTrendChart Component
 * Line chart showing performance score over time
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card } from '../common';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { PerformanceTrendPoint } from '../../types';
import { PERFORMANCE_THRESHOLDS, getScoreColor } from '../../types';

export interface PerformanceTrendChartProps {
  data: PerformanceTrendPoint[];
  title?: string;
  height?: number;
  showThresholds?: boolean;
  className?: string;
  isLoading?: boolean;
}

export const PerformanceTrendChart: React.FC<PerformanceTrendChartProps> = ({
  data,
  title = 'Performance Trend',
  height = 250,
  showThresholds = true,
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-40 mb-4" />
          <div className="bg-stone-200 rounded" style={{ height }} />
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div
          className="flex items-center justify-center text-stone-400"
          style={{ height }}
        >
          No trend data available
        </div>
      </Card>
    );
  }

  // Calculate trend direction
  const firstScore = data[0]?.score || 0;
  const lastScore = data[data.length - 1]?.score || 0;
  const scoreDiff = lastScore - firstScore;
  const trendDirection = scoreDiff > 2 ? 'up' : scoreDiff < -2 ? 'down' : 'stable';

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const score = payload[0].value;
      return (
        <div className="bg-white px-3 py-2 shadow-lg rounded-lg border border-stone-200">
          <p className="text-sm font-medium text-stone-800">{label}</p>
          <p className="text-sm" style={{ color: getScoreColor(score) }}>
            Score: <span className="font-bold">{score}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
        <div
          className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium',
            trendDirection === 'up' && 'bg-green-100 text-green-700',
            trendDirection === 'down' && 'bg-red-100 text-red-700',
            trendDirection === 'stable' && 'bg-stone-100 text-stone-600'
          )}
        >
          {trendDirection === 'up' && <TrendingUp className="w-4 h-4" />}
          {trendDirection === 'down' && <TrendingDown className="w-4 h-4" />}
          {trendDirection === 'stable' && <Minus className="w-4 h-4" />}
          <span>
            {scoreDiff > 0 ? '+' : ''}
            {scoreDiff.toFixed(0)} pts
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="periodLabel"
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            ticks={[0, 25, 50, 75, 100]}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Threshold reference lines */}
          {showThresholds && (
            <>
              <ReferenceLine
                y={PERFORMANCE_THRESHOLDS.EXCELLENT_MIN}
                stroke="#10B981"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
              <ReferenceLine
                y={PERFORMANCE_THRESHOLDS.GOOD_MIN}
                stroke="#3B82F6"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
              <ReferenceLine
                y={PERFORMANCE_THRESHOLDS.NEEDS_IMPROVEMENT_MIN}
                stroke="#F59E0B"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
            </>
          )}

          <Line
            type="monotone"
            dataKey="score"
            stroke="#3B82F6"
            strokeWidth={3}
            dot={{
              fill: '#3B82F6',
              strokeWidth: 2,
              r: 4,
            }}
            activeDot={{
              r: 6,
              fill: '#3B82F6',
              stroke: '#fff',
              strokeWidth: 2,
            }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Legend */}
      {showThresholds && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-stone-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-green-500 rounded" />
            <span>Excellent (90+)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-blue-500 rounded" />
            <span>Good (70+)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-amber-500 rounded" />
            <span>Needs Improvement (50+)</span>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PerformanceTrendChart;
